// Admin CRM & Leads — user contact/outreach management
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Download, Mail, Phone, Pencil, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const STATUS_OPTIONS = [
  { value: 'new', label: '🔵 New', bg: '#eff6ff', color: '#1d4ed8' },
  { value: 'called', label: '🟡 Called', bg: '#fef3c7', color: '#d97706' },
  { value: 'in_progress', label: '🟠 In Progress', bg: '#fff7ed', color: '#ea580c' },
  { value: 'lead', label: '🔷 Hot Lead', bg: '#f0f9ff', color: '#0284c7' },
  { value: 'converted', label: '🟢 Converted', bg: '#f0fdf4', color: '#16a34a' },
  { value: 'not_interested', label: '🔴 Not Interested', bg: '#fef2f2', color: '#dc2626' },
];

const statusMeta = (status) => STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const EMPTY_EDIT_FORM = { status: 'new', assigned_to: '', notes: '', follow_up_date: '' };

export const AdminCRM = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [premiumFilter, setPremiumFilter] = useState('all');

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/crm/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/crm/team`, { headers });
      setTeam(res.data.team || []);
    } catch (err) {
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (assignedFilter !== 'all') params.assigned_to = assignedFilter;
      if (premiumFilter !== 'all') params.premium = premiumFilter;

      const res = await axios.get(`${API_URL}/admin/crm/users`, { headers, params });
      setUsers(res.data.users || []);
      setPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load CRM users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, search, statusFilter, assignedFilter, premiumFilter]);

  useEffect(() => { fetchStats(); fetchTeam(); }, [fetchStats, fetchTeam]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const applyFilters = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const updateCRMStatus = async (userId, status) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, crm_status: status } : u)));
    try {
      await axios.put(`${API_URL}/admin/crm/users/${userId}`, { status }, { headers });
      fetchStats();
    } catch (err) {
      toast.error('Failed to update status');
      fetchUsers();
    }
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setEditForm({
      status: user.crm_status || 'new',
      assigned_to: user.crm_assigned_to || '',
      notes: user.crm_notes || '',
      follow_up_date: user.crm_follow_up_date ? user.crm_follow_up_date.slice(0, 10) : '',
    });
  };

  const closeEditModal = () => {
    setEditUser(null);
    setEditForm(EMPTY_EDIT_FORM);
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/crm/users/${editUser.id}`, {
        status: editForm.status,
        assigned_to: editForm.assigned_to || null,
        notes: editForm.notes,
        follow_up_date: editForm.follow_up_date || null,
      }, { headers });
      setUsers((prev) => prev.map((u) => (u.id === editUser.id ? { ...u, ...res.data } : u)));
      toast.success('Contact updated');
      fetchStats();
      closeEditModal();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { format: 'csv' };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (assignedFilter !== 'all') params.assigned_to = assignedFilter;
      if (premiumFilter !== 'all') params.premium = premiumFilter;

      const res = await axios.get(`${API_URL}/admin/crm/users`, { headers, params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'crm_users.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const statCards = stats ? [
    { label: 'Total', value: stats.total, color: '#374151' },
    { label: 'New', value: stats.by_status?.new || 0, color: '#1d4ed8' },
    { label: 'Called', value: stats.by_status?.called || 0, color: '#d97706' },
    { label: 'In Progress', value: stats.by_status?.in_progress || 0, color: '#ea580c' },
    { label: 'Leads', value: stats.by_status?.lead || 0, color: '#0284c7' },
    { label: 'Converted', value: stats.by_status?.converted || 0, color: '#16a34a' },
    { label: 'Follow-ups Today', value: stats.follow_ups_today || 0, color: '#A67C52' },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">CRM & Leads</h1>
        <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="crm-export-csv-btn">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {statCards.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search name, email, phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                className="pl-10"
                data-testid="crm-search-input"
              />
            </div>
          </div>
          <div className="w-40">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="crm-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Select value={assignedFilter} onValueChange={(v) => { setAssignedFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="crm-assigned-filter"><SelectValue placeholder="Assigned To" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Members</SelectItem>
                {team.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Select value={premiumFilter} onValueChange={(v) => { setPremiumFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="crm-premium-filter"><SelectValue placeholder="Premium" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Premium</SelectItem>
                <SelectItem value="false">Free</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={applyFilters} data-testid="crm-apply-filters-btn">Apply Filters</Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Premium</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Last Called</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No users found</TableCell></TableRow>
              ) : users.map((user) => {
                const meta = statusMeta(user.crm_status);
                return (
                  <TableRow key={user.id} data-testid={`crm-row-${user.id}`}>
                    <TableCell>
                      <p style={{ fontWeight: 600, margin: 0 }}>{user.name}</p>
                      <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                        Joined {formatDate(user.joined_at)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div>
                        {user.phone && (
                          <a href={`tel:${user.phone_country_code}${user.phone}`}
                             style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#374151', textDecoration: 'none', fontSize: 13, marginBottom: 4 }}>
                            📞 {user.phone_country_code} {user.phone}
                          </a>
                        )}
                        <a href={`mailto:${user.email}`}
                           style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#374151', textDecoration: 'none', fontSize: 13 }}>
                          ✉️ {user.email}
                        </a>
                        {user.phone && (
                          <a href={`https://wa.me/${(user.phone_country_code || '').replace('+', '')}${user.phone}`}
                             target="_blank" rel="noopener noreferrer"
                             style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#25D366', textDecoration: 'none', fontSize: 13, marginTop: 4 }}>
                            WhatsApp
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span style={{
                        background: user.is_premium ? '#fff8e1' : '#f3f4f6',
                        color: user.is_premium ? '#f59e0b' : '#6b7280',
                        border: `1px solid ${user.is_premium ? '#fde68a' : '#e5e7eb'}`,
                        borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                      }}>
                        {user.is_premium ? '⭐ Premium' : '○ Free'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <select
                        value={user.crm_status || 'new'}
                        onChange={(e) => updateCRMStatus(user.id, e.target.value)}
                        data-testid={`crm-status-select-${user.id}`}
                        style={{
                          border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12,
                          fontWeight: 600, cursor: 'pointer', background: meta.bg, color: meta.color,
                        }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell className="text-sm">{user.crm_assigned_to_name || '—'}</TableCell>
                    <TableCell className="text-sm">{formatDate(user.crm_last_contacted)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" title="Add notes / assign" onClick={() => openEditModal(user)} data-testid={`crm-edit-btn-${user.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Send email" asChild>
                          <a href={`mailto:${user.email}?subject=Thachari Premium Access`}>
                            <Mail className="w-4 h-4" />
                          </a>
                        </Button>
                        {user.phone && (
                          <Button size="icon" variant="ghost" title="Call" asChild>
                            <a href={`tel:${user.phone_country_code}${user.phone}`}>
                              <Phone className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} total contacts</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>Page {page} of {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit modal */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && closeEditModal()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contact — {editUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-1">Status</p>
              <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="crm-modal-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Assign to</p>
              <Select value={editForm.assigned_to || 'unassigned'} onValueChange={(v) => setEditForm((f) => ({ ...f, assigned_to: v === 'unassigned' ? '' : v }))}>
                <SelectTrigger data-testid="crm-modal-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {team.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Follow-up Date</p>
              <Input
                type="date"
                value={editForm.follow_up_date}
                onChange={(e) => setEditForm((f) => ({ ...f, follow_up_date: e.target.value }))}
                data-testid="crm-modal-followup"
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Notes</p>
              <Textarea
                rows={4}
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Call notes..."
                data-testid="crm-modal-notes"
              />
            </div>
            {editUser?.crm_history?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Call History</p>
                <div className="space-y-1 max-h-32 overflow-y-auto text-sm text-muted-foreground border rounded-lg p-3">
                  {editUser.crm_history.map((h, i) => (
                    <p key={i}>
                      • {formatDate(h.date)} — {statusMeta(h.status).label.replace(/^[^\s]+\s/, '')}
                      {h.notes ? `: ${h.notes}` : ''}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditModal}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="crm-modal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save & Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
